import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare, faCalendarEdit, faCalendarPlus, faClone, faRedo, faWrench,
} from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Grid, HoverHintWrapper, ModalContext, SectionDivider, SidebarModal, StatusBadge,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'
import { statusColors } from '@functions/status'
import useAgencyProviders from '@hooks/useAgencyProviders'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useManageOrderItem from './hooks/useManageOrderItem'

import Artwork from './components/Artwork'
import Notes from './components/Notes'
import OrderInstructions from './components/OrderInstructions'
import OrderItemActivity from './components/OrderItemActivity'
import OrderItemDetails from './components/OrderItemDetails'
import OrderItemErrors from './components/OrderItemErrors'
import PublicationDetails from './components/PublicationDetails'
import Tasks from './components/Tasks'

const MODAL_KEY = 'ManageOrderItemModal'

const requestOptions = {
  ...defaultRequestOptions.artwork,
  ...defaultRequestOptions.artworkPage,
  ...defaultRequestOptions.artworkReview,
  ...defaultRequestOptions.attachment,
  ...defaultRequestOptions.order,
  ...defaultRequestOptions.orderItem,
  ...defaultRequestOptions.orderItemTask,
  ...defaultRequestOptions.publication,
  ...defaultRequestOptions.publicationEdition,
}

type ModalCallbacks = {
  closeModal: () => void,
  duplicateOrderItem: (payload: HandleCallbackActionParams) => void,
  reorderItem: (payload: HandleCallbackActionParams) => void,
  updateOrderItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageOrderItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, duplicateOrderItem, reorderItem, updateOrderItem,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { orderItem: initOrderItem } = modalPayload

  const manageOrderItemPayload = useManageOrderItem(initOrderItem)

  const {
    callbacks: {
      addLiftover,
      createOrderWithOrderItem,
      deleteOrderItemFn,
      duplicateOrderItemFn,
      manageTasks,
      updateOrderItemFn,
      editPublicationEdition,
    },
    artworkRequired,
    orderItemPayload,
    requiredBy,
    showMoreOptions,
    creatingOrder,
  } = manageOrderItemPayload

  const {
    agency,
    order,
    orderItem,
    orderItem: {
      deep_link,
      deep_link_url,
      id,
    },
    canChangeEdition,
    filteredOrderItemTasks,
    isDeletable,
    isDuplicatable,
    isNotable,
    isPending,
    isPublicationOrder,
    isReorderable,
    isTaskable,
    requiredItems,
    status,
  } = orderItemPayload

  const agencyId = order?.agency_id ?? agency?.id
  const { provider_id, provider_price_list_id, service_id } = orderItem
  const { filteredAgencyProviders, loading: agencyProvidersLoading } = useAgencyProviders({
    filters: { agency_id: agencyId, provider_id, service_id },
    performHttpRequests: !!agencyId && !!provider_id && !!service_id,
  })
  const [currentAgencyProvider] = filteredAgencyProviders

  const priceListHasChanged = !!currentAgencyProvider?.provider_price_list_id
    && !!provider_price_list_id
    && currentAgencyProvider.provider_price_list_id !== provider_price_list_id

  const reorderItemPayload = {
    callbacks: {
      action: createOrderWithOrderItem,
      afterAction: closeModal,
    },
    entityParams: orderItem,
    requestOptions,
    shouldRedirect: true,
    toastText: 'Order Item Created',
  }

  const confirmDeleteParams = {
    callbacks: {
      deleteFn: deleteOrderItemFn,
      afterAction: closeModal,
    },
    strings: {
      entityName: 'Order Item',
    },
  }

  const duplicatePayload = {
    callbacks: {
      action: duplicateOrderItemFn,
      afterAction: closeModal,
    },
    entityParams: orderItem,
    requestOptions,
    toastText: 'Order Item Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Order Item" />

      <OrderItemDetails orderItemPayload={orderItemPayload} />

      <SidebarModal.Content>
        <Form.Field direction="column" label="Item Status">
          <StatusBadge
            // @ts-expect-error TS2538
            color={statusColors[status.key]}
            text={status.title}
          />

          <OrderItemErrors orderItemPayload={orderItemPayload} />

          {isPending && !isTaskable && (
            <Form.Field direction="column" label="Required By" marginTop="medium">
              <input
                onBlur={e => updateOrderItemFn(
                  // @ts-expect-error TS2353
                  { id, required_by: e.target.value },
                  { include_order_item_due_date: true },
                )}
                style={{ marginBottom: 0 }}
                type="datetime-local"
                defaultValue={requiredBy}
              />
            </Form.Field>
          )}
        </Form.Field>

        {artworkRequired && (<Artwork orderItemPayload={orderItemPayload} />)}

        {isPublicationOrder && <PublicationDetails orderItemPayload={orderItemPayload} />}

        {isPending && isNotable && (<Notes orderItem={orderItem} callbacks={{ updateOrderItem, updateOrderItemFn }} />)}

        {!isPending && (<OrderInstructions orderItem={orderItem} />)}

        <SectionDivider boxProps={{ marginTop: 'large' }}>
          More
        </SectionDivider>

        {isPending && !!filteredOrderItemTasks.length && (
          <Tasks orderItemPayload={orderItemPayload} />
        )}

        <OrderItemActivity orderItemPayload={orderItemPayload} />

        {showMoreOptions && (
          <Form.Field direction="column" label="More Options" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <Grid gridRowGap="medium">
              {isPublicationOrder && canChangeEdition && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCalendarEdit} />}
                  onClick={() => editPublicationEdition({
                    callbacks: {
                      selectPublicationEditionId: (payload: HandleCallbackActionParams) => {
                        const { callbacks: { afterAction }, entityParams } = payload
                        updateOrderItemFn({ id, ...entityParams })
                        // @ts-expect-error TS2322
                        if (afterAction) afterAction()
                      },
                    },
                  })}
                  size="medium"
                >
                  Change Edition
                </Button>
              )}

              {isPublicationOrder && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCalendarPlus} />}
                  onClick={() => addLiftover({ orderItemId: orderItem.id })}
                  size="medium"
                >
                  Add Liftover
                </Button>
              )}

              {isDuplicatable && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faClone} />}
                  onClick={() => duplicateOrderItem(duplicatePayload)}
                  size="medium"
                >
                  Duplicate Item
                </Button>
              )}

              {isDeletable && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faRedo} />}
                  loading={creatingOrder}
                  onClick={() => confirmDelete(confirmDeleteParams)}
                  size="medium"
                >
                  Delete Item
                </Button>
              )}

              {!isPending && isTaskable && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faWrench} />}
                  loading={creatingOrder}
                  onClick={() => manageTasks()}
                  size="medium"
                >
                  Manage Tasks
                </Button>
              )}

              {isReorderable && (
                priceListHasChanged ? (
                  <HoverHintWrapper
                    boxProps={{ display: 'inline-block', position: 'relative', width: '100%' }}
                    hintComponent={(
                      <HoverHintWrapper.Hint boxProps={{ bottom: 'calc(100% + 5px)', top: 'auto' }}>
                        Reordering is disabled as the price list associated with this
                        <br />
                         item has changed.
                        Please place a new order instead.
                      </HoverHintWrapper.Hint>
                    )}
                  >
                    <Button
                      buttonStyle="secondaryUtility"
                      disabled
                      icon={<FontAwesomeIcon icon={faRedo} />}
                      size="medium"
                    >
                      Reorder Item
                    </Button>
                  </HoverHintWrapper>
                ) : (
                  <Button
                    buttonStyle="secondaryUtility"
                    disabled={agencyProvidersLoading}
                    icon={<FontAwesomeIcon icon={faRedo} />}
                    loading={creatingOrder}
                    // @ts-expect-error TS2345
                    onClick={() => reorderItem(reorderItemPayload)}
                    size="medium"
                  >
                    Reorder Item
                  </Button>
                )
              )}

              {!!deep_link_url && (
                <Button
                  // @ts-expect-error TS2322
                  as="a"
                  buttonStyle="secondaryUtility"
                  href={deep_link_url}
                  icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                  rel="noopener noreferrer"
                  size="medium"
                  target="_blank"
                >
                  {deep_link?.link_text || 'Manage Item'}
                </Button>
              )}
            </Grid>
          </Form.Field>
        )}
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageOrderItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal